class ShopLooksCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.shop-looks-carousel__track');
    this.prevBtn = this.querySelector('.shop-looks-carousel__arrow--prev');
    this.nextBtn = this.querySelector('.shop-looks-carousel__arrow--next');
    this.dotsWrap = this.querySelector('.shop-looks-carousel__dots');
    this.slides = this.track
      ? Array.from(this.track.children).filter((el) => el.classList.contains('shop-looks-slide'))
      : [];

    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseFloat(this.dataset.autoplaySpeed) || 4000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.track || this.slides.length === 0) return;

    // Oculta, sin animacion, el espaciador invisible del inicio (edge
    // buffer) para que la primera foto quede pegada al borde visualmente.
    this.track.scrollLeft = this.edgeBuffer();

    this.buildDots();
    this.updateArrows();
    this.bindEvents();

    if (this.autoplay && !this.reducedMotion && this.slides.length > this.itemsPerView()) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  edgeBuffer() {
    const raw = getComputedStyle(this).getPropertyValue('--edge-buffer');
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  itemsPerView() {
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    const raw = getComputedStyle(this).getPropertyValue(desktop ? '--items-desktop' : '--items-mobile');
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : desktop ? 4 : 2;
  }

  pageCount() {
    return Math.max(1, Math.ceil(this.slides.length / this.itemsPerView()));
  }

  currentPage() {
    if (!this.track.clientWidth) return 0;
    const raw = Math.round((this.track.scrollLeft - this.edgeBuffer()) / this.track.clientWidth);
    return Math.max(0, Math.min(raw, this.pageCount() - 1));
  }

  goToPage(index) {
    const pageWidth = this.track.clientWidth;
    const maxIndex = this.pageCount() - 1;
    const clamped = Math.max(0, Math.min(index, maxIndex));
    this.track.scrollTo({ left: this.edgeBuffer() + clamped * pageWidth, behavior: 'smooth' });
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    const pages = this.pageCount();
    for (let i = 0; i < pages; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'shop-looks-carousel__dot';
      dot.setAttribute('aria-label', 'Ir a la pagina ' + (i + 1));
      dot.addEventListener('click', () => this.goToPage(i));
      this.dotsWrap.appendChild(dot);
    }
    this.updateDots();
  }

  updateDots() {
    if (!this.dotsWrap) return;
    const current = this.currentPage();
    Array.from(this.dotsWrap.children).forEach((dot, i) => {
      dot.setAttribute('aria-current', i === current ? 'true' : 'false');
    });
  }

  updateArrows() {
    const current = this.currentPage();
    const last = this.pageCount() - 1;
    if (this.prevBtn) this.prevBtn.disabled = current <= 0;
    if (this.nextBtn) this.nextBtn.disabled = current >= last;
  }

  bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goToPage(this.currentPage() - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goToPage(this.currentPage() + 1));

    let ticking = false;
    this.track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.updateDots();
        this.updateArrows();
        ticking = false;
      });
    }, { passive: true });

    ['mouseenter', 'focusin', 'touchstart'].forEach((evt) => {
      this.addEventListener(evt, () => this.stopAutoplay(), { passive: true });
    });
    ['mouseleave', 'focusout'].forEach((evt) => {
      this.addEventListener(evt, () => {
        if (this.autoplay && !this.reducedMotion) this.startAutoplay();
      });
    });

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.track.scrollLeft = this.edgeBuffer() + this.currentPage() * this.track.clientWidth;
        this.buildDots();
        this.updateArrows();
      });
      this.resizeObserver.observe(this);
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      const next = this.currentPage() + 1;
      if (next > this.pageCount() - 1) {
        this.goToPage(0);
      } else {
        this.goToPage(next);
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

if (!customElements.get('shop-looks-carousel')) {
  customElements.define('shop-looks-carousel', ShopLooksCarousel);
}
